import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { STATUS, rp, num, dt, MOVE, photoUrl } from "@/lib/api";

export const PageHeader = ({ title, sub, children }) => (
  <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-5 fade-up">
    <div>
      <h1 className="text-2xl lg:text-3xl font-bold tracking-tight text-slate-900" data-testid="page-title">{title}</h1>
      {sub && <p className="text-sm text-slate-500 mt-1 max-w-2xl">{sub}</p>}
    </div>
    <div className="flex gap-2 flex-wrap">{children}</div>
  </div>
);

export const StatusBadge = ({ s, testid }) => {
  const [l, c] = STATUS[s] || [s, "bg-slate-100 text-slate-700 border-slate-300"];
  return <span data-testid={testid || "status-badge"} className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold border whitespace-nowrap ${c}`}><span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />{l}</span>;
};

export const AckBadge = ({ doc, testid }) => {
  const ack = doc.acknowledged;
  const [l, c] = ack
    ? [`Direview ✓${doc.ack_by?.name ? ` · ${doc.ack_by.name}` : ""}`, "bg-emerald-100 text-emerald-800 border-emerald-300"]
    : ["Perlu review", "bg-amber-100 text-amber-800 border-amber-300"];
  return <span data-testid={testid || "ack-badge"} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border whitespace-nowrap ${c}`}>{l}</span>;
};

const TAG = { slate: "bg-slate-100 text-slate-700", emerald: "bg-emerald-100 text-emerald-700", amber: "bg-amber-100 text-amber-700", rose: "bg-rose-100 text-rose-700", indigo: "bg-indigo-100 text-indigo-700", sky: "bg-sky-100 text-sky-700" };
export const Tag = ({ children, tone = "slate" }) => <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${TAG[tone]}`}>{children}</span>;

export const Card = ({ title, action, children, className = "", testid }) => (
  <section data-testid={testid} className={`bg-white border border-slate-200 rounded-lg shadow-sm fade-up ${className}`}>
    {title && <header className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100"><h3 className="text-sm font-semibold text-slate-800">{title}</h3>{action}</header>}
    {children}
  </section>
);

export const Sel = ({ value, onChange, options, placeholder, testid, className = "", disabled }) => (
  <select data-testid={testid} disabled={disabled} className={`inp ${className}`} value={value ?? ""} onChange={(e) => onChange(e.target.value)}>
    {placeholder !== undefined && <option value="">{placeholder}</option>}
    {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
  </select>
);

export const Field = ({ label, children, className = "" }) => (
  <label className={`block ${className}`}><span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1 block">{label}</span>{children}</label>
);

export const Money = ({ v, className = "" }) => {
  const { can } = useAuth();
  if (!can("finance.view")) return null;
  return <span className={`num ${v < 0 ? "text-rose-600" : ""} ${className}`}>{rp(v)}</span>;
};
const isMoney = (el) => !!el && typeof el === "object" && !Array.isArray(el) && (el.type === Money || isMoney(el.props?.children));
export const MoneyOnly = ({ children }) => {
  const { can } = useAuth();
  return can("finance.view") ? children : null;
};
export const Qty = ({ v, u, className = "" }) => <span className={`num ${className}`}>{num(v)} <span className="text-slate-400 text-[11px]">{u}</span></span>;

export const Table = ({ cols: allCols, rows, testid, onRow, empty = "Belum ada data" }) => {
  const { can } = useAuth();
  const money = can("finance.view");
  const cols = allCols.filter((c) => c && (money || !(c.m || (rows[0] && c.r && isMoney(c.r(rows[0]))))));
  return (
  <div className="overflow-x-auto" data-testid={testid}>
    <table className="tbl w-full">
      <thead><tr>{cols.map((c) => <th key={c.h} className={c.right ? "!text-right" : ""}>{c.h}</th>)}</tr></thead>
      <tbody>
        {rows.length === 0 && <tr><td colSpan={cols.length} className="!text-center !py-10 text-slate-400">{empty}</td></tr>}
        {rows.map((r, i) => (
          <tr key={r.id || i} onClick={onRow ? () => onRow(r) : undefined} className={onRow ? "cursor-pointer" : ""} data-testid={testid ? `${testid}-row-${i}` : undefined}>
            {cols.map((c) => <td key={c.h} className={`${c.right ? "text-right" : ""} ${c.cls || ""}`}>{c.r ? c.r(r) : r[c.k]}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
  );
};

export const KV = ({ items }) => {
  const { can } = useAuth();
  const money = can("finance.view");
  return (
  <dl className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3 p-4">
    {items.filter((x) => x && (money || !(x[2] === "m" || isMoney(x[1])))).map(([k, v]) => (
      <div key={k}><dt className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">{k}</dt><dd className="text-sm text-slate-900 mt-0.5">{v || "-"}</dd></div>
    ))}
  </dl>
  );
};

export const Photos = ({ list, label }) => !list?.length ? null : (
  <div className="p-4">
    <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-2">{label}</div>
    <div className="flex gap-2 flex-wrap">
      {list.map((p) => (
        <a key={p.id} href={photoUrl(p.id)} target="_blank" rel="noreferrer" className="block" data-testid="photo-thumb">
          <img src={photoUrl(p.id)} alt={p.filename} className="w-24 h-24 object-cover rounded border border-slate-200" />
          <div className="text-[10px] text-slate-500 mt-1 w-24 truncate">{dt(p.uploaded_at)}</div>
        </a>
      ))}
    </div>
  </div>
);

export const AuditTimeline = ({ items }) => (
  <ol className="p-4 space-y-4" data-testid="audit-timeline">
    {items.length === 0 && <li className="text-sm text-slate-400">Belum ada catatan</li>}
    {items.map((a) => (
      <li key={a.id} className="relative pl-6">
        <span className="absolute left-0 top-1.5 w-2.5 h-2.5 rounded-full bg-emerald-600 ring-4 ring-emerald-100" />
        <div className="text-[11px] text-slate-500 num">{dt(a.ts)} · <span className="font-semibold text-slate-700">{a.user?.name}</span></div>
        <div className="text-[13px] font-semibold text-slate-800">{a.action}</div>
        {a.detail && <div className="text-[13px] text-slate-600">{a.detail}</div>}
      </li>
    ))}
  </ol>
);

export const LedgerTable = ({ rows, locById, showLoc = true }) => (
  <Table testid="ledger-table" rows={rows} empty="Belum ada posting ledger (stok belum bergerak)" cols={[
    { h: "Waktu", r: (r) => <span className="num text-xs">{dt(r.ts)}</span> },
    { h: "Ref", r: (r) => <span className="num text-xs font-semibold">{r.ref_code}</span> },
    { h: "Jenis", r: (r) => MOVE[r.movement_type] || r.movement_type },
    { h: "Barang", k: "item_name" },
    showLoc && { h: "Lokasi", r: (r) => locById?.[r.location_id]?.name || "-" },
    { h: "Asal/Tujuan", k: "counterparty" },
    { h: "Masuk", right: true, r: (r) => r.qty_in ? <Qty v={r.qty_in} u={r.unit} className="text-emerald-700" /> : "" },
    { h: "Keluar", right: true, r: (r) => r.qty_out ? <Qty v={r.qty_out} u={r.unit} className="text-rose-600" /> : "" },
    { h: "Biaya/unit", right: true, r: (r) => <Money v={r.unit_cost} /> },
    { h: "Nilai", right: true, r: (r) => <Money v={r.value} className="font-semibold" /> },
    { h: "Oleh", r: (r) => r.user?.name },
  ].filter(Boolean)} />
);

export const Stat = ({ label, value, icon: Icon, tone = "slate", to, testid, hint }) => {
  const tones = { slate: "text-slate-700 bg-slate-100", amber: "text-amber-700 bg-amber-100", sky: "text-sky-700 bg-sky-100", rose: "text-rose-700 bg-rose-100", emerald: "text-emerald-700 bg-emerald-100", indigo: "text-indigo-700 bg-indigo-100" };
  const body = (
    <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-[box-shadow,transform] duration-200 h-full" data-testid={testid}>
      <div className="flex items-start justify-between gap-2">
        <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">{label}</div>
        {Icon && <span className={`p-1.5 rounded-md ${tones[tone]}`}><Icon size={15} /></span>}
      </div>
      <div className="mt-2 text-xl 2xl:text-lg font-bold text-slate-900 num tracking-tight whitespace-nowrap">{value}</div>
      {hint && <div className="text-[11px] text-slate-500 mt-1">{hint}</div>}
    </div>
  );
  return to ? <Link to={to} className="block">{body}</Link> : body;
};
